import { createInertiaApp } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import '../css/app.css';
import { initializeTheme } from './composables/useAppearance';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

/**
 * Nom de la marque tel que le serveur l'a écrit dans <meta name="application-name">
 * (App\Support\Seo::brand()). Lu au besoin, car le <head> existe avant ce script.
 */
function brandName(): string {
    if (typeof document === 'undefined') return appName;

    const meta = document.querySelector<HTMLMetaElement>(
        'meta[name="application-name"]',
    );

    return meta?.content || appName;
}

/**
 * Ajoute la marque en suffixe, sauf si le titre la contient déjà. Même règle
 * que App\Support\Seo::title() : les pages publiques arrivent déjà suffixées
 * par le serveur, les pages d'administration ne le sont pas.
 */
export function formatTitle(title: string): string {
    const brand = brandName();

    if (!title) return brand;

    return title.toLowerCase().includes(brand.toLowerCase())
        ? title
        : `${title} — ${brand}`;
}

createInertiaApp({
    title: formatTitle,
    resolve: (name) =>
        resolvePageComponent(
            `./pages/${name}.vue`,
            import.meta.glob<DefineComponent>('./pages/**/*.vue'),
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN as string,
            environment:
                (import.meta.env.VITE_APP_ENV as string) ?? 'production',
            enabled: !!import.meta.env.VITE_SENTRY_DSN,
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: 0.2,
            replaysOnErrorSampleRate: 1.0,
        });

        app.use(plugin).mount(el);
    },
    progress: {
        color: '#c17f24',
    },
});

// This will set light / dark mode on page load...
initializeTheme();
